import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Arrow-driven scroll track for the "Complete the perfect pair" cards. One card fills the track,
 * so each arrow moves by one track width. Arrows disable at either end.
 *
 * @extends {Component<{
 *   track: HTMLElement,
 *   previous: HTMLButtonElement,
 *   next: HTMLButtonElement,
 * }>}
 */
class OudhPairTrack extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.refs.track?.addEventListener('scroll', this.#update, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.track?.removeEventListener('scroll', this.#update);
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }

  /**
   * Opens the variant drawer named by the clicked button.
   * @param {Event} event
   */
  open(event) {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest("[data-drawer]") : null);
    const drawer = button?.dataset.drawer ? document.getElementById(button.dataset.drawer) : null;
    if (drawer instanceof HTMLDialogElement && !drawer.open) drawer.showModal();
  }

  /** @param {number} direction */
  #scroll(direction) {
    const { track } = this.refs;
    if (!track) return;

    track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  }

  #update = () => {
    const { track, previous, next } = this.refs;
    if (!track || !previous || !next) return;

    const max = track.scrollWidth - track.clientWidth;
    previous.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max - 1;
  };
}

if (!customElements.get('oudh-pair-track')) {
  customElements.define('oudh-pair-track', OudhPairTrack);
}

/**
 * Variant choice inside the pair drawer. Picking an option finds the variant that matches every
 * selected value, then points the form at it and shows its price. No match, or a sold-out match,
 * disables the button.
 *
 * The add itself is product-form-component's. The drawer closes only once that add has succeeded,
 * so an error (sold out, quantity limit) stays on screen next to the button that caused it.
 */
class OudhPairVariants extends Component {
  /** @type {{ id: number, options: string[], available: boolean, price: string }[]} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    const data = this.querySelector('script[data-pair-variants]');
    this.#variants = data?.textContent ? JSON.parse(data.textContent) : [];

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#dialog?.addEventListener('click', this.#onBackdrop);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#dialog?.removeEventListener('click', this.#onBackdrop);
  }

  get #dialog() {
    return this.closest('dialog');
  }

  #onChange = () => {
    // Radios and selects both carry data-option-index; read one value per option, in option order.
    /** @type {string[]} */
    const selected = [];
    this.querySelectorAll('[data-option-index]').forEach((control) => {
      const field = /** @type {HTMLInputElement | HTMLSelectElement} */ (control);
      if (field instanceof HTMLInputElement && !field.checked) return;
      selected[Number(field.dataset.optionIndex)] = field.value;
    });
    const match = this.#variants.find((variant) => variant.options.every((value, index) => value === selected[index]));

    const id = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pair-id]'));
    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pair-submit]'));
    const price = this.querySelector('[data-pair-price]');

    if (id && match) id.value = String(match.id);
    if (price && match) price.textContent = match.price;
    if (submit) submit.disabled = !match || !match.available;
  };

  /** @param {Event} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-pair-close]')) this.#dialog?.close();
  };

  /** @param {MouseEvent} event */
  #onBackdrop = (event) => {
    if (event.target === this.#dialog) this.#dialog?.close();
  };

  /**
   * Closing hands over to the cart drawer: Horizon defers its auto-open until an open modal
   * dialog that started the add has closed.
   * @param {Event} event
   */
  #onCartLinesUpdate = (event) => {
    const { promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);

    promise
      ?.then(({ detail }) => {
        if (!detail?.didError) this.#dialog?.close();
      })
      .catch(() => {
        // Leave the drawer open; product-form-component shows its own error.
      });
  };
}

if (!customElements.get('oudh-pair-variants')) {
  customElements.define('oudh-pair-variants', OudhPairVariants);
}
